'use client';
import { cn } from '@/lib/utils';
import React, { useEffect, useRef, useState } from 'react';
import styles from './table-of-content.module.scss';

export interface TableOfContentItem {
  label: string;
  href?: string;
}

interface TableOfContentProps {
  title?: string;
  items: TableOfContentItem[];
  activeIndex?: number;
  onItemClick?: (index: number) => void;
  sticky?: boolean;
}

const TableOfContent: React.FC<TableOfContentProps> = ({
  title = 'Table of contents',
  items,
  activeIndex: controlledActiveIndex,
  onItemClick,
  sticky = true,
}) => {
  // // Helper function to standardize label text
  // const standardizeLabel = (label: string): string => {
  //   return capitalize(label.trim().toLowerCase());
  // };

  // Scrollspy logic with Intersection Observer
  const [activeIndex, setActiveIndex] = useState<number>(controlledActiveIndex ?? 0);
  const isControlled = controlledActiveIndex !== undefined;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeItemRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    if (isControlled) setActiveIndex(controlledActiveIndex!);
  }, [controlledActiveIndex, isControlled]);

  // Auto-scroll to active item when activeIndex changes
  useEffect(() => {
    const activeItemElement = activeItemRefs.current[activeIndex];
    const scrollContainer = scrollContainerRef.current;

    if (activeItemElement && scrollContainer) {
      // Check if the active item is visible in the scroll container
      const containerRect = scrollContainer.getBoundingClientRect();
      const itemRect = activeItemElement.getBoundingClientRect();

      const isVisible =
        itemRect.top >= containerRect.top && itemRect.bottom <= containerRect.bottom;

      if (!isVisible) {
        activeItemElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }
  }, [activeIndex]);

  useEffect(() => {
    if (isControlled) return;
    const headingElements = items.map((item) => {
      if (!item.href) return null;
      const id = item.href.replace(/^#/, '');
      return document.getElementById(id);
    });
    if (headingElements.every((h) => !h)) return;

    const visibleMap: Record<string, number> = {};

    const handleIntersections = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.target && entry.target.id) {
          visibleMap[entry.target.id] = entry.intersectionRatio;
        }
      });
      // Find the heading with the highest intersection ratio
      let maxIdx = 0;
      let maxRatio = 0;
      items.forEach((item, idx) => {
        if (!item.href) return;
        const id = item.href.replace(/^#/, '');
        const ratio = visibleMap[id] || 0;
        if (ratio > maxRatio) {
          maxRatio = ratio;
          maxIdx = idx;
        }
      });
      setActiveIndex(maxIdx);
    };

    const observer = new window.IntersectionObserver(handleIntersections, {
      root: null,
      rootMargin: '-100px 0px 0px 0px', // offset for sticky header
      threshold: Array.from({ length: 11 }, (_, i) => i / 10),
    });
    observerRef.current = observer;
    headingElements.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [items, isControlled]);

  return (
    <div
      className={styles.component + (sticky ? ' ' + styles.stickyPosition : '')}
      aria-label={title}
    >
      <div className={styles.component_wrapper}>
        <h5 className={styles.component_wrapper_title}>{title}</h5>
        <div className={styles.component_wrapper_divider} />
        <div className={styles.component_wrapper_scrollableContainer} ref={scrollContainerRef}>
          <nav>
            <ul className={styles.component_wrapper_scrollableContainer_list}>
              {items.map((item, idx) => (
                <li
                  key={item.label}
                  ref={(el) => {
                    activeItemRefs.current[idx] = el;
                  }}
                  className={cn(
                    styles.component_wrapper_scrollableContainer_list_item,
                    idx === activeIndex &&
                      styles.component_wrapper_scrollableContainer_list_item_active,
                  )}
                >
                  <a
                    href={item.href || '#'}
                    onClick={(e) => {
                      e.preventDefault();
                      if (item.href) {
                        const id = item.href.replace(/^#/, '');
                        const el = document.getElementById(id);
                        if (el) {
                          window.scrollTo({
                            top: el.getBoundingClientRect().top + window.scrollY - 120,
                            behavior: 'smooth',
                          });
                        }
                      }
                      onItemClick?.(idx);
                      if (!isControlled) setActiveIndex(idx);
                    }}
                    aria-current={idx === activeIndex ? 'true' : undefined}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default TableOfContent;
